<template>
  <div class="tooltip" :class="{ active: active }">
    <button
      ref="tooltipItem"
      class="tooltip__item"
      :class="{ 'tooltip__item--bottom': isPosBottom, active: active }"
      @mouseover="showTooltip(true)"
      @click="showTooltip(true)"
      @focus="focusClose()"
      @keyup.esc="escape()"
    >
      <span class="transition-bg-left">
        <slot></slot>
      </span>
    </button>
    <div
      ref="tooltip"
      class="tooltip__content"
      role="tooltip"
      :aria-hidden="ariaHidden"
      :style="{ width: widthSize, left: leftSizeMobile }"
      :class="{
        'tooltip__content--left': isPosLeft,
        'tooltip__content--right': isPosRight,
        'tooltip__content--bottom': isPosBottom,
        'tooltip__content--image': hasImage,
        'tooltip__content--image-only': hasImageOnly,
      }"
    >
      <span v-if="hasImage" class="tooltip__image">
        <media-image
          :filename="imageSettings.image"
          :alt="imageSettings.alt"
          :sizes="imageSettings.sizes"
          :resolutions="imageSettings.resolutions"
          :lazy="imageSettings.lazy"
          :fallback-size="imageSettings.fallbacksize"
        >
        </media-image>
      </span>
      <p class="tooltip__description" v-html="currentText"></p>
      <button ref="tooltipClose" class="tooltip__close" @click="showTooltip(false)">
        <svg class="icon">
          <title>Close</title>
          <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#close-icon"></use>
        </svg>
      </button>
    </div>
    <div v-if="active" class="tooltip__overlay" @click="showTooltip(false)"></div>
  </div>
</template>

<script>
import debounce from 'lodash/debounce';

export default {
  name: 'tooltip',
  props: {
    text: { type: String, required: false },
    imageSettings: { type: Object, required: false },
    tagging: { type: Object, required: true },
  },
  data() {
    return {
      active: false,
      ariaHidden: 'true',
      currentText: this.text,
      hasImage: false,
      hasImageOnly: false,
      isPosLeft: false,
      isPosRight: false,
      isPosBottom: false,
      isMobileDisplay: false,
      marginMobile: 40,
      marginLeftMobile: 20,
      widthSize: null,
      leftSizeMobile: null,
      smallScreen: window.ScreenSizeHandler
        ? window.ScreenSizeHandler.getMqForSize('small').selected
        : true,
      number: 1,
      analyticsFired: false,
    };
  },
  mounted() {
    /* istanbul ignore next */
    if (window.eventBus) {
      window.eventBus.$on('tooltip.state', (value) => {
        this.active = value;
      });
    }

    this.hasImage = !!this.imageSettings;
    this.hasImageOnly = !!this.imageSettings && !this.text;

    this.tooltipLeftPosition();

    /* istanbul ignore next */
    window.eventBus.$on('mediaquery.changed', () => {
      this.smallScreen = window.ScreenSizeHandler
        ? window.ScreenSizeHandler.getMqForSize('small').selected
        : true;
    });

    /* istanbul ignore next */
    window.addEventListener(
      'orientationchange',
      () => {
        this.screenWidth();
        window.eventBus.$emit('tooltip.state', false);
        this.$nextTick(() => {
          this.tooltipLeftPosition();
        });
        if (document.querySelector('tooltip-over')) {
          document.querySelector('body').classList.remove('tooltip-over');
        }
      },
      false,
    );
  },
  created() {
    window.addEventListener('resize', debounce(this.tooltipLeftPosition, 5));
  },
  destroyed() {
    window.removeEventListener('resize', this.tooltipLeftPosition);
  },
  methods: {
    showTooltip(value) {
      this.$nextTick(() => {
        this.tooltipTopPosition();
      });
      window.eventBus.$emit('tooltip.state', false);
      this.active = value;
      this.ariaHidden = !value ? 'true' : 'false';

      if (this.active) {
        document.querySelector('body').classList.add('tooltip-over');
        if (!this.analyticsFired) {
          this.fireAnalytics();
          this.analyticsFired = true;
        }
      } else {
        document.querySelector('body').classList.remove('tooltip-over');
        this.analyticsFired = false;
      }

      this.$nextTick(() => {
        if (this.active) {
          this.$refs.tooltipClose.focus();
        }
      });
    },
    screenWidth() {
      return document.documentElement.clientWidth || document.body.clientWidth;
    },
    tooltipWidth() {
      return this.$refs.tooltip.clientWidth;
    },
    mobileDisplay() {
      this.widthSize = `${this.screenWidth() - this.marginMobile}px`;
      this.leftSizeMobile = `-${this.$refs.tooltipItem.getBoundingClientRect().left - this.marginLeftMobile}px`;
      this.isMobileDisplay = true;
    },
    tooltipLeftPosition() {
      const leftValue = this.$refs.tooltip.getBoundingClientRect().left;
      const rightValue = this.$refs.tooltip.getBoundingClientRect().right;
      const tooltopHalfWidth = this.tooltipWidth() / 2;

      if (this.smallScreen && !this.hasImageOnly) {
        this.mobileDisplay();
      } else {
        this.widthSize = 'auto';
        this.leftSizeMobile = null;

        if (leftValue <= 0 && this.screenWidth() > rightValue) {
          this.isPosLeft = true;
          this.isPosRight = false;
        } else if (this.screenWidth() < rightValue + tooltopHalfWidth) {
          this.isPosLeft = false;
          this.isPosRight = true;
        } else {
          this.isPosLeft = false;
          this.isPosRight = false;
        }
      }
    },
    tooltipTopPosition() {
      const tooltip = this.$refs.tooltip;
      const topValue = this.$refs.tooltipItem.getBoundingClientRect().top;
      let tooltipHeight = tooltip.clientHeight;
      const headerEl = document.querySelector('.header');
      if (headerEl) {
        tooltipHeight += headerEl.clientHeight;
      }

      if (topValue <= tooltipHeight) {
        this.isPosBottom = true;
      } else {
        this.isPosBottom = false;
      }
    },
    focusClose() {
      this.showTooltip(true);
      if (this.active) {
        this.$refs.tooltipClose.focus();
      }
    },
    escape() {
      this.showTooltip(false);
    },
    fireAnalytics() {
      const tagging = {
        event: this.tagging.event,
        eventCategory: this.tagging.eventCategory,
        eventAction: this.tagging.eventAction,
        eventLabel: this.tagging.eventLabel,
      };
      try {
        dataLayer.push(tagging);
      } catch {
        console.warn(tagging);
      }
    },
  },
};
</script>
